import type { TranslationShape } from "./en";

/**
 * Portuguese (pt-PT) translations. Must mirror the English shape exactly; the
 * `npm run check:i18n` script enforces key parity.
 */
export const pt: TranslationShape = {
  appName: "Sprint",
  common: {
    loading: "A carregar…",
    error: "Ocorreu um erro",
    retry: "Tentar novamente",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    remove: "Remover",
    close: "Fechar",
    confirm: "Confirmar",
    yes: "Sim",
    no: "Não",
    back: "Voltar",
    next: "Seguinte",
    previous: "Anterior",
    done: "Concluído",
    submit: "Submeter",
    search: "Pesquisar",
    filter: "Filtrar",
    reset: "Repor",
    refresh: "Atualizar",
    copy: "Copiar",
    copied: "Copiado",
    empty: "Ainda não há nada aqui.",
    notAvailable: "N/D",
    unknown: "Desconhecido",
    open: "Abrir",
    add: "Adicionar",
    create: "Criar",
    remove_confirm: "Tem a certeza que quer remover?",
    notFoundTitle: "Página não encontrada",
    notFoundBody: "A página que procura não existe.",
    backHome: "Voltar ao início",
    english: "English",
    portuguese: "Português",
    consoleSubtitle: "Consola do operador",
    adminSubtitle: "Administração",
    all: "Todos",
    none: "Nenhum",
    optional: "opcional",
    required: "obrigatório",
    from: "De",
    to: "Até",
    range: "Intervalo",
    today: "Hoje",
    id: "ID",
    details: "Detalhes",
    more: "Mais",
  },
  theme: {
    light: "Claro",
    dark: "Escuro",
    system: "Sistema",
    toggle: "Alternar tema",
  },
  nav: {
    dashboard: "Jornada",
    matches: "Jogos",
    teams: "Equipas",
    players: "Atletas",
    sync: "Atualizações",
    club: "Clube",
    kits: "Equipamentos",
    admin: "Administração",
    logout: "Terminar sessão",
    settings: "Definições",
    language: "Idioma",
    primary: "Navegação principal",
    openMenu: "Abrir menu",
  },
  login: {
    title: "Bem-vindo ao Sprint",
    clubLabel: "URL Emjogo",
    clubDescription: "O URL que usa para aceder ao EmJogo do seu clube.",
    usernameLabel: "Utilizador EmJogo",
    passwordLabel: "Palavra-passe EmJogo",
    submit: "Entrar",
    loadingSubmit: "A entrar…",
    clubLink: "Entrar como operador de clube",
    errors: {
      invalid: "Utilizador ou palavra-passe inválidos.",
      notAuthorized:
        "Este clube não está autorizado a usar o Sprint. Contacte o operador se for um engano.",
      usernameRequired: "É necessário introduzir o utilizador.",
      passwordRequired: "É necessário introduzir a palavra-passe.",
      clubRequired: "Introduza o URL do EmJogo do seu clube.",
    },
  },
  admin: {
    loginTitle: "Sessão de administrador",
    loginSubtitle: "Gerir quais os clubes que podem usar o Sprint.",
    usernameLabel: "Utilizador administrador",
    passwordLabel: "Palavra-passe administrador",
    clubs: {
      title: "Clubes autorizados",
      subtitle: "Adicionar, atualizar ou suspender clubes que usam o Sprint.",
      add: "Adicionar clube",
      clubname: "Identificador",
      displayName: "Nome apresentado",
      notes: "Notas",
      active: "Ativo",
      authorizedAt: "Autorizado",
      lastLogin: "Último acesso",
      updatedAt: "Atualizado",
      empty:
        "Ainda não há clubes autorizados. Adicione o primeiro no botão acima.",
      createDialogTitle: "Autorizar novo clube",
      createDialogDescription:
        "O identificador deve coincidir exatamente com o usado no EmJogo.",
      editDialogTitle: "Atualizar clube",
      suspendConfirm:
        "Tem a certeza? Suspender um clube impede imediatamente que os operadores iniciem sessão.",
      statusActive: "Ativo",
      statusSuspended: "Suspenso",
      authorizedBy: "Autorizado por",
      createdBy: "Autorizado por {{name}}",
      neverLoggedIn: "Nunca iniciou sessão",
      suspend: "Suspender",
      activate: "Reativar",
      errors: {
        duplicate: "Já existe um clube autorizado com esse identificador.",
        empty: "Tem de fornecer pelo menos um campo.",
        notFound: "Clube não encontrado.",
      },
      toast: {
        created: "Clube autorizado.",
        updated: "Clube atualizado.",
        suspended: "Clube suspenso.",
        activated: "Clube reativado.",
      },
    },
  },
  dashboard: {
    title: "Jogos",
    subtitle:
      "Os jogos da sua equipa, próximos e recentes, com o estado de submissão.",
    filters: {
      dateRange: "Intervalo de datas",
      allTeams: "Todas as equipas",
      team: "Equipa",
      competition: "Prova",
      submission: "Submissão",
      submitted: "Submetido ao Score",
      notSubmitted: "Por submeter",
      upcoming: "Próximos",
      past: "Passados",
      clear: "Limpar filtros",
    },
    columns: {
      date: "Data",
      match: "Jogo",
      venue: "Local",
      competition: "Prova",
      status: "Estado",
      actions: "Ações",
    },
    venueHome: "Casa",
    venueAway: "Fora",
    vs: "vs",
    at: "em",
    openSheet: "Abrir ficha de jogo",
    empty:
      "Sem jogos neste intervalo. Alargue o período ou faça uma sincronização.",
    refreshAll: "Atualizar dados de jornada",
    refreshAllDescription:
      "Obtém a informação mais recente do EmJogo para todos os jogos próximos.",
    refreshSubmitted: "Verificar submissões ao Score",
    today: "Hoje",
    thisWeek: "Esta semana",
    thisMonth: "Este mês",
    nextMonth: "Próximo mês",
  },
  matches: {
    sheet: {
      title: "Ficha de jogo",
      breadcrumb: "Ficha · {{match}}",
      modeSinglePage: "Página única",
      modeWizard: "Assistente passo-a-passo",
      modeHelp: "Alternar entre edição densa e um fluxo guiado.",
      refreshFromEmjogo: "Atualizar do EmJogo",
      refreshFromEmjogoHint:
        "Volta a obter os detalhes e a convocatória deste jogo do EmJogo.",
      sections: {
        summary: "Jogo",
        kit: "Equipamento",
        staff: "Staff",
        roster: "Convocados",
        starters: "Onze inicial",
        bench: "Suplentes",
        submission: "Submissão ao Score",
      },
      kit: {
        none: "Nenhum equipamento selecionado",
        change: "Trocar equipamento",
        select: "Selecionar equipamento",
        makeDefault: "Definir como principal",
        oneOffDescription:
          "Escolha as cores apenas para este jogo, sem alterar os equipamentos guardados.",
        saveKit: "Guardar como equipamento reutilizável",
        outfield: {
          sectionTitle: "Equipamento de campo",
          oneOffTitle: "Equipamento de campo avulso",
          oneOffLabel: "Equipamento avulso",
          addOneOff: "Cores avulsas",
        },
        goalkeeper: {
          sectionTitle: "Equipamento de guarda-redes",
          oneOffTitle: "Equipamento de guarda-redes avulso",
          oneOffLabel: "Equipamento avulso",
          addOneOff: "Cores avulsas",
          none: "Sem equipamento de guarda-redes",
        },
      },
      staff: {
        copyPrevious: "Copiar do jogo anterior",
        copySource: "Copiado do jogo com {{opponent}} ({{date}})",
        emptyEmjogo: "Sem staff aprovado no Score para este clube.",
        emptyScore: "Sem staff aprovado no Score para este clube.",
        function: "Função",
        banco: "Banco suplementar",
        custom: "Função personalizada",
        picker: "Selecionar staff",
        saveChanges: "Guardar staff",
        search: "Procurar por nome ou função…",
        searchHint: "A pesquisa cobre todas as categorias.",
        selectedCount: "{{count}} selecionado(s)",
        selectedTitle: "Selecionados",
        selectedEmpty:
          "Ainda não selecionou ninguém. Escolha uma categoria abaixo ou procure pelo nome para adicionar quem vai estar no banco.",
        availableTitle: "Adicionar staff",
        availableCount: "{{count}} disponíve(is)",
        add: "Adicionar",
        addAria: "Adicionar {{name}} à ficha",
        removeAria: "Remover {{name}} da ficha",
        noMatchInCategory: "Ainda não há ninguém nesta categoria.",
        noSearchMatches: 'Sem resultados para "{{query}}".',
        allInCategoryAdded: "Toda a gente desta categoria já está na ficha.",
        categories: {
          coaches: "Treinadores",
          delegates: "Delegados",
          medical: "Equipa médica",
          others: "Outros",
        },
        functions: {
          trainerMain: "Treinador principal",
          trainerAssistant: "Treinador adjunto",
          physicalCoach: "Preparador físico",
          delegate1: "1º Delegado",
          delegate2: "2º Delegado",
          medic: "Médico",
          masseur: "Massagista",
          nurse: "Enfermeiro",
          trainerIntern: "Treinador estagiário",
          physiotherapist: "Fisioterapeuta",
          other: "Outra",
          sbvTechnician: "Técnico de SBV-DAE",
        },
      },
      players: {
        role: "Papel",
        roleC: "Capitão",
        roleSC: "Sub-capitão",
        number: "Número",
        numberPlaceholder: "#",
        license: "Licença",
        resolved: "Resolvido",
        ambiguous: "Ambíguo",
        unresolved: "Por resolver",
        crossTeam: "Outra equipa",
        pick: "Escolher jogador",
        picker: "Selecionar jogador",
        pickerHint: "Pesquise por nome, número ou licença.",
        preview: "Detalhes do jogador: {{name}}",
        manualNote: "Introduzido manualmente no EmJogo",
        noCandidates: "Sem jogadores correspondentes.",
      },
      submission: {
        notLinked: "Este jogo ainda não está associado ao Score.",
        linkScore: "Associar ao Score",
        unlinkScore: "Desassociar do Score",
        save: "Guardar no Score",
        submit: "Submeter ao Score",
        submitted: "Submetido ao Score",
        reverify: "Re-verificar",
        openReport: "Abrir PDF da ficha",
        openScore: "Abrir no Score",
        invalidLicenseTitle: "Jogadores sem licença válida",
        invalidLicenseBody:
          "Resolva os jogadores abaixo antes de submeter a ficha.",
        diffTitle: "Diferenças face ao Score",
        diffOk: "A ficha submetida corresponde ao que está no Score.",
        diffExtra: "A mais no Score",
        diffMissing: "Em falta no Score",
        diffMismatched: "Desacertado",
        diffError:
          "Não foi possível validar a submissão no Score ({{detail}}).",
      },
      linkDialog: {
        title: "Associar a um jogo no Score",
        description:
          "Escolha o jogo no Score que corresponde a este jogo EmJogo. Será usado para submeter a ficha.",
        empty: "Sem jogos disponíveis no Score para esta equipa.",
        refresh: "Atualizar jogos Score",
        chooseTeam: "Escolher a sua equipa no Score",
        chooseMatch: "Escolher o jogo adversário no Score",
      },
      modeAria: "Layout da ficha",
      wizard: {
        back: "Voltar",
        next: "Seguinte",
        progress: "Passo {{current}} de {{total}}",
        stepsAria: "Passos da ficha de jogo",
        linkKitDescription:
          "Associe este jogo ao Score e escolha o equipamento que a equipa vai usar.",
        rosterDescription:
          "Reveja o onze inicial e os suplentes lado a lado: resolva entradas ambíguas, defina números e indique o capitão e o subcapitão.",
        staffDescription:
          "Escolha o staff que vai constar na ficha, com as respetivas funções.",
        reviewDescription:
          "Passo final: guardar a ficha no Score, submeter e verificar.",
        step: {
          linkKit: "Jogo e equipamento",
          roster: "Convocados",
          staff: "Staff",
          review: "Rever e submeter",
        },
        summary: {
          title: "Resumo da ficha",
          link: "Score",
          notLinked: "Ainda não associado",
          linkedAs: "Score #{{id}}",
          kit: "Equipamento",
          noKit: "Sem equipamento escolhido",
          gkKit: "Equipamento de guarda-redes",
          starters: "Titulares",
          bench: "Suplentes",
          startersCount: "{{count}} titular",
          startersCount_other: "{{count}} titulares",
          benchCount: "{{count}} suplente",
          benchCount_other: "{{count}} suplentes",
          captain: "Capitão: {{name}}",
          viceCaptain: "Subcapitão: {{name}}",
          unresolvedWarning: "{{count}} por resolver",
          ambiguousWarning: "{{count}} ambíguos",
          staff: "Staff",
          staffCount: "{{count}} selecionado",
          staffCount_other: "{{count}} selecionados",
          noStaff: "Nenhum staff selecionado",
          bench_staff: "Banco suplementar: {{count}}",
        },
      },
    },
  },
  teams: {
    title: "Equipas",
    subtitle:
      "Todas as equipas sincronizadas do EmJogo, com o número de atletas licenciados.",
    searchPlaceholder: "Pesquisar por equipa, escalão ou categoria",
    openDetail: "Abrir equipa",
    scoreLinked: "Ligada ao Score",
    scoreNotLinked: "Sem ligação ao Score",
    category: "Categoria",
    empty: "Ainda não há equipas sincronizadas.",
    emptyHint:
      "Faça uma sincronização completa do clube para obter as equipas do EmJogo.",
    emptyFiltered: "Nenhuma equipa corresponde aos filtros atuais.",
    clearFilters: "Limpar filtros",
    filters: {
      level: "Escalão",
      allLevels: "Todos os escalões",
    },
    stats: {
      players: "Atletas",
      matches: "Jogos",
    },
    detail: {
      backToTeams: "Todas as equipas",
      syncFromEmjogo: "Atualizar do EmJogo",
      rosterTitle: "Plantel",
      rosterDescription:
        "Atletas licenciados que o EmJogo tem associados a esta equipa.",
      rosterSearchPlaceholder: "Pesquisar atletas…",
      noPlayers: "Esta equipa ainda não tem atletas.",
      noPlayersDescription:
        "Sincronize esta equipa (ou o clube completo) para obter o plantel licenciado.",
      noMetadata: "Ainda não há metadados sincronizados para esta equipa.",
      notFound: "Equipa não encontrada",
      notFoundHint:
        "Esta equipa pode ter sido removida ou não pertence ao seu clube.",
    },
  },
  players: {
    title: "Atletas",
    subtitle: "{{count}} atleta licenciado em todas as equipas.",
    subtitle_other: "{{count}} atletas licenciados em todas as equipas.",
    searchPlaceholder: "Pesquisar por nome, número, licença ou equipa",
    empty: "Ainda não há atletas sincronizados.",
    emptyHint:
      "Faça uma sincronização completa do clube para obter o plantel licenciado.",
    noMatches: "Nenhum atleta corresponde aos filtros atuais.",
    columns: {
      name: "Atleta",
      team: "Equipa",
      position: "Posição",
      license: "Licença",
    },
    filters: {
      position: "Posição",
      allPositions: "Todas as posições",
    },
  },
  demo: {
    banner: "Modo de demonstração — todos os dados são fictícios",
    reset: {
      run: "Repor demo",
      toastSuccess: "Demo reposta — o clube está de novo vazio.",
    },
    seed: {
      run: "Carregar dados",
      toastSuccess:
        "Carregadas {{teams}} equipas, {{players}} jogadores e {{matches}} jogos.",
    },
  },
  debug: {
    title: "Dados de debug",
    subtitle:
      "Ferramentas apenas para desenvolvimento local, para iniciar sessão, repor e semear dados de teste.",
    login: {
      title: "Iniciar sessão como clube de teste",
      description:
        "Inicia sessão como o clube de teste através do fluxo real de autenticação contra o emjogo-mock, incluindo uma sessão Score (score-mock) automática — sincronizações, jobs e submissões funcionam todos.",
      run: "Iniciar sessão como clube de teste",
      toastSuccess: "Sessão iniciada como clube de teste.",
    },
    reset: {
      title: "Repor base de dados",
      description: "Apaga todas as coleções da base de dados local.",
      run: "Repor base de dados",
      toastSuccess: "Base de dados reposta.",
    },
    seed: {
      title: "Carregar dados mock",
      description:
        "Carrega o mundo de teste dos mocks (equipas, jogadores, staff, jogos) diretamente para a base de dados, incluindo ligações ao Score — sem correr o pipeline de sincronização. Repõe primeiro se os dados foram semeados antes de uma mudança de ids.",
      run: "Carregar dados mock",
      toastSuccess:
        "Carregadas {{teams}} equipas, {{players}} jogadores, {{matches}} jogos ({{linkedMatches}} ligados ao Score).",
    },
  },
  sync: {
    title: "Atualizações",
    subtitle:
      "O Sprint guarda uma cópia dos dados do clube vindos do EmJogo. Quando algo muda lá, traga-o para o Sprint a partir daqui.",
    requires: "Requer sessão {{systems}}.",
    lastRun: "Última execução {{when}}",
    neverRun: "Ainda não foi executado.",
    matches: {
      title: "Atualizar jogos e convocatórias",
      description:
        "Obtém os jogos, convocatórias e onzes mais recentes do EmJogo. Corra isto antes de preparar uma ficha de jogo.",
      run: "Atualizar jogos",
    },
    full: {
      title: "Sincronização completa",
      description:
        "Reimporta tudo — equipas, atletas, staff e licenças — e alinha com o Score. Use quando entraram atletas ou equipas novas no EmJogo, ou quando algo parecer errado. Demora alguns minutos.",
      run: "Executar sincronização completa",
    },
    advanced: {
      title: "Opções avançadas",
      description:
        "Ferramentas específicas para correções pontuais — a sincronização completa já cobre tudo isto.",
    },
    team: {
      title: "Atualizar uma equipa",
      description:
        "Reimporta jogos, staff e atletas licenciados de uma única equipa.",
      run: "Atualizar equipa",
      teamLabel: "Equipa",
      teamPlaceholder: "Escolher equipa",
    },
    licenses: {
      title: "Preencher licenças em falta",
      description:
        "Procura no EmJogo os números de licença dos atletas que ainda não os têm.",
      run: "Obter licenças",
    },
    activity: {
      title: "Atividade recente",
      description:
        "Cada atualização corre em segundo plano. Abra uma entrada para ver os detalhes.",
    },
  },
  jobs: {
    empty: "Ainda não há atividade. Corra a primeira atualização acima.",
    columns: {
      type: "Tipo",
      status: "Estado",
      created: "Criada",
      started: "Iniciada",
      finished: "Concluída",
      duration: "Duração",
      progress: "Progresso",
      error: "Erro",
    },
    type: {
      full_sync_club: "Sincronização completa",
      sync_team: "Sincronizar equipa",
      refresh_all_matches: "Atualizar todos os jogos",
      refresh_match: "Atualizar um jogo",
      sync_missing_licenses: "Sincronizar licenças",
    },
    status: {
      queued: "Em espera",
      running: "Em execução",
      succeeded: "Concluída",
      partial_failure: "Parcialmente concluída",
      failed: "Falhou",
      cancelled: "Cancelada",
    },
    stepStatus: {
      pending: "Pendente",
      running: "A correr",
      succeeded: "Concluído",
      failed: "Falhou",
      skipped: "Ignorado",
    },
    details: {
      title: "Tarefa · {{id}}",
      params: "Parâmetros",
      steps: "Passos",
      result: "Resultado",
      error: "Erro",
      cancel: "Cancelar tarefa",
      cancelConfirm:
        "Cancelar esta tarefa? O trabalho já em execução irá terminar.",
      cancelSuccess: "Tarefa cancelada.",
      cancelFailed:
        "Não foi possível cancelar — pode já estar em execução ou terminada.",
    },
    duration: "{{value}}",
    stepKey: {
      resolve_teams: "Resolver equipas",
      sync_matches: "Sincronizar jogos",
      sync_athletes: "Sincronizar atletas",
      sync_licenses: "Sincronizar licenças",
      link_score: "Associar ao Score",
      sync_staff: "Sincronizar staff",
      sync_score_match: "Sincronizar jogo Score",
    },
  },
  score: {
    reauthTitle: "Entrar no Score para continuar",
    reauthDescription:
      "Esta ação precisa de uma sessão Score renovada. Introduza as suas credenciais para continuar.",
    usernameLabel: "Utilizador Score",
    passwordLabel: "Palavra-passe Score",
    codeLabel: "Código de verificação",
    codeHint: "Introduza o código único enviado pelo Score.",
    startLogin: "Enviar código de verificação",
    verify: "Verificar",
    resend: "Reenviar código",
    expiresSoon: "A sua sessão Score expira em {{minutes}} minutos.",
    signedIn: "Sessão Score ativa.",
    signOut: "Terminar sessão Score",
    errors: {
      invalid: "Utilizador ou palavra-passe inválidos.",
      invalidCode: "Código de verificação inválido ou expirado.",
      upstream:
        "Não conseguimos contactar o Score. Tente novamente daqui a um momento.",
    },
  },
  club: {
    profile: {
      title: "Perfil do clube",
      subtitle: "Informação sincronizada do EmJogo.",
      fullname: "Designação completa",
      name: "Designação curta",
      shortname: "Abreviatura",
      notSyncedYet:
        "Ainda não há dados do EmJogo. Faça uma sincronização completa para preencher o nome, o emblema e a lista de equipas.",
    },
    kits: {
      edit: "Editar",
      nameLabel: "Nome do equipamento",
      shirt: "Camisola",
      shorts: "Calções",
      socks: "Meias",
      outfield: {
        title: "Equipamentos de campo",
        subtitle: "Equipamentos dos jogadores de campo usados em jogo.",
        add: "Adicionar equipamento de campo",
        edit: "Editar equipamento de campo",
        empty: "Ainda não há equipamentos de campo. Adicione um para o usar nas fichas de jogo.",
      },
      goalkeeper: {
        title: "Equipamentos de guarda-redes",
        subtitle: "Equipamentos de guarda-redes, escolhidos separadamente em cada jogo.",
        add: "Adicionar equipamento de guarda-redes",
        edit: "Editar equipamento de guarda-redes",
        empty: "Ainda não há equipamentos de guarda-redes.",
      },
      default: "Principal",
      setDefault: "Definir como principal",
      clearDefault: "Remover principal",
      deleteConfirm:
        "Eliminar este equipamento? As fichas que o usam ficam sem equipamento associado.",
      empty:
        "Ainda não há equipamentos. Adicione o primeiro para o usar em fichas de jogo.",
      toast: {
        created: "Equipamento adicionado.",
        updated: "Equipamento atualizado.",
        deleted: "Equipamento eliminado.",
        defaultSet: "Equipamento principal atualizado.",
        defaultCleared: "Equipamento principal removido.",
      },
      color: {
        Red: "Vermelho",
        Black: "Preto",
        Yellow: "Amarelo",
        White: "Branco",
        Blue: "Azul",
        Green: "Verde",
        Golden: "Dourado",
        Orange: "Laranja",
        Gray: "Cinzento",
        Maroon: "Grená",
        "White-Blue": "Alvi-Celeste",
        Lilac: "Lilás",
        Pink: "Rosa",
        "White-Black": "Alvi-Negra",
      },
    },
  },
  errors: {
    title: "Não foi possível carregar",
    networkTitle: "Sem rede ou servidor indisponível",
    networkBody:
      "Não conseguimos contactar o Sprint. Verifique a ligação e tente novamente.",
    genericBody: "Ocorreu um erro inesperado. Tente novamente.",
    unauthorizedTitle: "Sessão terminada",
    unauthorizedBody: "Inicie sessão novamente para continuar.",
    forbiddenTitle: "Sem permissões",
    forbiddenBody: "A sua conta não pode aceder a este recurso.",
    sessionMissingTitle: "Sessão em falta",
    sessionMissingBody:
      "Por favor, inicie sessão novamente para podermos aceder ao seu clube em segurança.",
  },
  footer: {
    status: "Todos os sistemas operacionais",
    build: "Build {{version}}",
    docs: "Documentação",
  },
};
